import * as React from 'react';
import { ButtonList, ComplexValue, DateTime, Numberbox, Validation, ValidationProps } from 'ui';
import { RecurringRangeOptions, RecurrencePattern, ScheduleFieldKeys, TIME_MIDNIGHT_STRING } from 'app/area/company-report/constants';
import { SelectSingleRadioButton } from 'ui/select-single-short-list/select-single-radio-button/select-single-radio-button';
import { api } from '../api';
import { useEffect, useState } from 'react';
import { ScheduleDefinition } from 'app/area/company-report/types-store';
import { createFormatters, createUTCDate, dateToUTC, parseAsDateUTC } from 'app/infrastructure/data/date';
import { DailySchedule } from './schedule-form-daily';
import { WeeklySchedule } from './schedule-form-weekly';
import { MonthlySchedule } from './schedule-form-monthly';
import { YearlySchedule } from './schedule-form-yearly';
import { toUTCDateWithLocalOffset, updateWithRangeEndAfterOccurences, updateWithRangeEndByDate, updateWithRangeOfRecurrenceOption,
  updateWithRangeStartDay, updateWithExecutionTime, onChangeCreator, newDateOrUndefined } from './schedule-helpers';
import { deepEquals } from 'app/infrastructure/js';
import { TimePicker } from 'ui/date-time/time/time';
import styles from './report-screen.scss';

interface ScheduleFormProps {
  scheduleDefinition: ScheduleDefinition;
  onUpdateScheduleDefinition: (scheduleDefinition: ScheduleDefinition, validateSchedule: boolean) => void;
  locale: string;
  translation: any;
  getValidationErrors: (key: string) => ValidationProps;
}

// eslint:disable-next-line: max-func-body-length
export const ScheduleForm = ({
  scheduleDefinition,
  onUpdateScheduleDefinition,
  locale,
  translation,
  getValidationErrors
}: ScheduleFormProps) => {

  const { 
    start, occurences, weeks_on, days, of_every, months, years, of, recur_every, execution_time,
    every, every_weekday, no_end_date, end_after, end_by, day, the, on_the, on, done, empty_date_error } = translation;

  const [workingScheduleDefinition, setScheduleDefinition] = useState<ScheduleDefinition>(scheduleDefinition);
  const [recurrencePatternOptions, setRecurrencPatternOptions] = useState([]);
  const [scheduleWeekdayOptions, setScheduleWeekdayOptions] = useState([]);
  const [scheduleMonthOptions, setScheduleMonthOptions] = useState([]);
  const [scheduleOccurenceOptions, setScheduleOccurenceOptions] = useState([]);
  const [selectedRecurrencePattern, setRecurrencePattern] = useState(undefined);
  const selectedScheduleStartDate = toUTCDateWithLocalOffset(newDateOrUndefined(scheduleDefinition?.recurRangeStart));
  const selectedRangeOptionValue = scheduleDefinition?.recurrenceRange?.value;
  const selectedExecutionTime = toUTCDateWithLocalOffset(newDateOrUndefined(scheduleDefinition?.recurRangeStart));
  const selectedEndAfterOccurences = scheduleDefinition?.recurRangeEndAfter;
  const selectedRangeEndByDate = toUTCDateWithLocalOffset(newDateOrUndefined(scheduleDefinition?.recurRangeEndBy));

  const getRecurrencePatternOptions = async () => {
    const options = await api.getRecurrencePatternOptions();
    setRecurrencPatternOptions(options);
    return options;
  };

  const getInitialOptions = async () => {
    setScheduleWeekdayOptions((await api.getScheduleWeekDays()));
    setScheduleMonthOptions((await api.getScheduleMonths()));
    setScheduleOccurenceOptions((await api.getScheduleOccurences()));
  };

  useEffect(() => {
    if (!!workingScheduleDefinition && !!scheduleDefinition && !deepEquals(workingScheduleDefinition, scheduleDefinition)) {
      onUpdateScheduleDefinition(workingScheduleDefinition, false);
    }
  }, [workingScheduleDefinition, onUpdateScheduleDefinition, scheduleDefinition]);
 
  useEffect(() => { 
    setRecurrencePattern(scheduleDefinition?.recurrencePattern);
    }, [recurrencePatternOptions, scheduleDefinition?.recurrencePattern]
  );

  useEffect(() => {
    getInitialOptions();
  }, []);

  const onChangeRecurrencePattern = (val: ComplexValue<number>) => {
    if (!workingScheduleDefinition) {
      return;
    }
    setRecurrencePattern(val);
    setScheduleDefinition(prevState => ({ ...prevState, recurrencePattern: val }));
  };
  const onChangeRangeStartDay = onChangeCreator(updateWithRangeStartDay, setScheduleDefinition);
  const onChangeRangeOfRecurrenceOption = onChangeCreator(updateWithRangeOfRecurrenceOption, setScheduleDefinition);
  const onChangeEndAfterOccurences = onChangeCreator(updateWithRangeEndAfterOccurences, setScheduleDefinition);
  const onChangeRangeEndByDate = onChangeCreator(updateWithRangeEndByDate, setScheduleDefinition);
  const onChangeExecutionTime = onChangeCreator(updateWithExecutionTime, setScheduleDefinition);
  
  const recurrencePatternTabsContent = {
    [RecurrencePattern.Daily]: <DailySchedule
      setScheduleDefinition={setScheduleDefinition}
      scheduleDefinition={scheduleDefinition}
      translations={{every, every_weekday, days}}
      locale={locale}
      getValidationErrors={getValidationErrors}
    />,
    [RecurrencePattern.Weekly]: <WeeklySchedule
      setScheduleDefinition={setScheduleDefinition}
      scheduleDefinition={scheduleDefinition}
      scheduleWeekdayOptions={scheduleWeekdayOptions}
      translations={{recur_every, weeks_on}}
      locale={locale}
      getValidationErrors={getValidationErrors}
    />,
    [RecurrencePattern.Monthly]: <MonthlySchedule
      setScheduleDefinition={setScheduleDefinition}
      scheduleDefinition={scheduleDefinition}
      scheduleWeekdayOptions={scheduleWeekdayOptions}
      occurenceOptions={scheduleOccurenceOptions}
      translations={{day, the, of_every, months}}
      locale={locale}
      getValidationErrors={getValidationErrors}
    />,
    [RecurrencePattern.Yearly]: <YearlySchedule
      setScheduleDefinition={setScheduleDefinition}
      scheduleDefinition={scheduleDefinition}
      scheduleMonthOptions={scheduleMonthOptions}
      scheduleWeekdayOptions={scheduleWeekdayOptions}
      occurenceOptions={scheduleOccurenceOptions}
      translations={{recur_every, years, months, on, on_the, of}}
      locale={locale}
      getValidationErrors={getValidationErrors}
     />
  };

  const isSelectedRangeOption = (value: number): boolean => selectedRangeOptionValue === value;

  const recurPatternValidationClass = !!getValidationErrors(ScheduleFieldKeys.recurrencePattern) ? 'has-danger' : '';
  const executionTimeValidation = getValidationErrors(ScheduleFieldKeys.executionTime);

  return !!scheduleDefinition && <>
    <div className="row">
      <div className="col-12">
        <ButtonList
          componentId={ScheduleFieldKeys.recurrencePattern}
          className={recurPatternValidationClass}
          valueItem={selectedRecurrencePattern}
          onChange={onChangeRecurrencePattern}
          options={recurrencePatternOptions}
          onLoadOptions={getRecurrencePatternOptions}
          validation={getValidationErrors(ScheduleFieldKeys.recurrencePattern)}
        />
      </div>
    </div>

    {recurrencePatternTabsContent[selectedRecurrencePattern?.value]}

    <hr />

    <div className={`${styles.scheduleRow}`}>
      <div className={styles.textWrapper}>
        <span className={styles.texts}>{execution_time}</span>
      </div>
      <div className="col-sm-6 col-md-4">
        <TimePicker
          locale={locale}
          componentId={ScheduleFieldKeys.executionTime}
          className="dateTime"
          value={selectedExecutionTime}
          onChange={onChangeExecutionTime}
          parseDate={parseAsDateUTC}
          {...createFormatters(locale)}
          createDate={createUTCDate}
          cloneDate={dateToUTC}
          labelConfirmTimeSelection={done}
          defaultValue={TIME_MIDNIGHT_STRING}
          textEmptyDateError={empty_date_error}
        />
        {executionTimeValidation && <Validation {...executionTimeValidation} />}
      </div>
      <div className={styles.textWrapper}>
        <span className={styles.texts}>{start}</span>
      </div>
      <div className="col-sm-6 col-md-4">
        <DateTime 
          locale={locale}
          componentId={ScheduleFieldKeys.recurRangeStart}
          className={`dateTime ${styles.scheduleDateTime}`}
          value={selectedScheduleStartDate}
          onChange={onChangeRangeStartDay}
          parseDate={parseAsDateUTC}
          {...createFormatters(locale)}
          createDate={createUTCDate}
          cloneDate={dateToUTC}
          captionWrapper={'form'}
          validation={getValidationErrors(ScheduleFieldKeys.recurRangeStart)}
        />
      </div>
    </div>
    <div className={` ${styles.scheduleRow} ${selectedRangeOptionValue === undefined ? 'has-danger' : ''}`}>
      <div>
        <SelectSingleRadioButton
          componentId={`${ScheduleFieldKeys.recurrenceRange}-no-end-date`}
          onChange={onChangeRangeOfRecurrenceOption}
          isSelected={isSelectedRangeOption(RecurringRangeOptions.NoEndDate)}
          option={{ value: RecurringRangeOptions.NoEndDate, description: no_end_date }}
          validation={getValidationErrors(ScheduleFieldKeys.recurrenceRange)}
        />
      </div>
    </div>
    <div className={` ${styles.scheduleRow}`}>
      <div className={styles.textWrapper}>
        <SelectSingleRadioButton
          componentId={`${ScheduleFieldKeys.recurrenceRange}-end-after`}
          onChange={onChangeRangeOfRecurrenceOption}
          isSelected={isSelectedRangeOption(RecurringRangeOptions.EndAfter)}
          option={{ value: RecurringRangeOptions.EndAfter, description: end_after }}
          validation={getValidationErrors(ScheduleFieldKeys.recurrenceRange)}
        />
      </div>
      <div>
        <Numberbox
          componentId={ScheduleFieldKeys.recurRangeEndAfter}
          value={selectedEndAfterOccurences}
          onChange={onChangeEndAfterOccurences}
          className={styles.scheduleNumberbox}
          readOnly={!isSelectedRangeOption(RecurringRangeOptions.EndAfter)}
          locale={locale}
          validation={getValidationErrors(ScheduleFieldKeys.recurRangeEndAfter)}
          maxValue={999999999}
        />
      </div>
      <div className={styles.textWrapper}>
        <span className={styles.texts}>{occurences}</span>
      </div>
    </div>
    <div className={`${styles.scheduleRow}`}>
      <div className={styles.textWrapper}>
        <SelectSingleRadioButton
          componentId={`${ScheduleFieldKeys.recurrenceRange}-end-by`}
          onChange={onChangeRangeOfRecurrenceOption}
          isSelected={isSelectedRangeOption(RecurringRangeOptions.EndBy)}
          option={{ value: RecurringRangeOptions.EndBy, description: end_by }}
          validation={getValidationErrors(ScheduleFieldKeys.recurrenceRange)}
        />
      </div>
      <div className="col-sm-6 col-md-4">
        <DateTime
          locale={locale}
          componentId={ScheduleFieldKeys.recurRangeEndBy}
          className={`dateTime ${styles.scheduleDateTime}`}
          value={selectedRangeEndByDate}
          onChange={onChangeRangeEndByDate}
          parseDate={parseAsDateUTC}
          {...createFormatters(locale)}
          createDate={createUTCDate}
          cloneDate={dateToUTC}
          readOnly={!isSelectedRangeOption(RecurringRangeOptions.EndBy)}
          validation={getValidationErrors(ScheduleFieldKeys.recurRangeEndBy)}
          useTime={false}
          captionWrapper={'form'}
        />
      </div>
    </div>
  </>;
};
